/**
 * @Author: v_jppjpeng
 * @Date: 2023-12-19 16:11:33
 * @Explain: 选项列表
 */

import { intlGet } from '@/locales';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { Checkbox, List } from 'tdesign-react';
// type
import { SearchBox } from '@/components/tdesign';
import type { CommonOptionsType } from '@/definition/common/data.d';
import { doesItExist, isArrayLen } from '@/utils/common/dataJudgment';
import type { OptionListProps } from '../data.d';
// components
import Tooltip from '../toolTip';
// utils
import { defaultListMaxHeight } from '../const';

function OptionList(props: OptionListProps) {
  const {
    searchable,
    searchPlaceholder,
    allOption,
    multiple,
    autofocus,
    options,
    value,
    displayTextKey,
    emptyText = intlGet('COMMON.NO.DATA'),
    listHeader,
    listMaxHeight = defaultListMaxHeight,
    customOption,
    onScroll,
    onClose,
    onClick,
    onChange,
  } = props;

  const [searchValue, setSearchValue] = useState<any>();

  const onText = (item: any) => intlGet(item.labelKey) || item.label || '';

  // 列表
  const list: any[] = useMemo(
    () =>
      searchValue
        ? options.filter((item) => {
            const text: any = onText(item);
            return doesItExist(searchValue, text, true);
          })
        : allOption
        ? [{ label: intlGet('COMMON.ALL'), value: 'all' }, ...options]
        : options,
    [options, searchValue, allOption],
  );

  /** 重组参数 */
  const getItem = (item: CommonOptionsType) => {
    const newItem: any = { ...item };

    const text: any = onText(newItem);
    const tips: any = intlGet(newItem.tipsKey) || newItem.tips || '';
    const dataTitle: any =
      intlGet(newItem.dataTitleKey) || newItem.dataTitle || '';
    const textRender = tips ? (
      <Tooltip showArrow={false} content={tips} {...newItem?.tooltipProps}>
        {text}
      </Tooltip>
    ) : customOption ? (
      customOption({
        ...newItem,
        option: newItem,
      })
    ) : (
      (displayTextKey && newItem[displayTextKey]) || text
    );
    return {
      text,
      tips,
      dataTitle,
      textRender,
    };
  };

  // 列表渲染
  const listItem = useCallback(
    (item: CommonOptionsType) => {
      const { text, tips, dataTitle, textRender } = getItem(item);
      const commonProps = {
        title: '',
      };

      if (!tips || dataTitle) {
        commonProps.title = dataTitle || text;
      }

      return (
        <li
          key={item.value}
          {...commonProps}
          className={classNames('t-list-item', {
            'is-selected': value === item.value,
            'is-disabled': item.disabled,
          })}
          onClick={() => {
            if (!item.disabled) {
              onClose?.();
              item?.onClick?.(item.value, item) || onClick?.(item.value, item);
            }
          }}
        >
          <div className="t-list-item-main">{textRender}</div>
        </li>
      );
    },
    [value, onClick],
  );

  return (
    <>
      {/* 搜索框 */}
      {searchable && (
        <div className="dropdown-search-box">
          <SearchBox
            autofocus={autofocus}
            size="full"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(val) => {
              setSearchValue(val);
            }}
          />
        </div>
      )}

      {/* 列表 - 头部 */}
      {listHeader}

      {/* 列表 */}
      {isArrayLen(list) ? (
        <>
          {multiple ? (
            <Checkbox.Group
              className="td-checkbox-group"
              style={{ maxHeight: listMaxHeight }}
              value={value}
              onChange={(val, { current, type }) => {
                const allKey = 'all';
                let cVal = val;
                if (allOption) {
                  if (current === allKey) {
                    if (type === 'check') {
                      cVal = list.map((item) => item.value);
                    } else {
                      cVal = [];
                    }
                  } else {
                    if (type === 'uncheck') {
                      if (val.length !== list.length) {
                        cVal = cVal.filter((v: string) => v !== allKey);
                      }
                    } else {
                      if (val.length === list.length - 1) {
                        cVal.push(allKey);
                      }
                    }
                  }
                }
                onChange?.(cVal, {});
              }}
            >
              <List
                className="td-scroll-list"
                style={{ maxHeight: listMaxHeight }}
                onScroll={onScroll}
              >
                {list.map((item) => {
                  const { text, tips, textRender } = getItem(item);
                  const commonProps = {
                    title: tips ? tips : text,
                  };
                  return (
                    <li
                      key={item.value}
                      className="t-list-item"
                      {...commonProps}
                    >
                      <div className="t-list-item-main">
                        <Checkbox value={item.value} disabled={item.disabled}>
                          {textRender}
                        </Checkbox>
                      </div>
                    </li>
                  );
                })}
              </List>
            </Checkbox.Group>
          ) : (
            <List
              className="td-scroll-list"
              style={{ maxHeight: listMaxHeight }}
              onScroll={onScroll}
            >
              {list.map((item) => listItem(item))}
            </List>
          )}
        </>
      ) : (
        // 暂无数据
        <div className="td-popup-empty-text">{emptyText}</div>
      )}
    </>
  );
}

export default OptionList;
